import type { ReactNode } from 'react';

import { OnBandSurface } from './PageHeading';
import { StatusBadge, type StatusTone } from './StatusBadge';

/**
 * The template's document screens — Dreams ERP `sales/cashSalesDetails`, `sales/addCashSales`,
 * `purchase/purchaseOrderDetails`, `purchase/addPurchaseOrder` — rebuilt once for every sale and
 * purchase document (2026-10-22), never imported.
 *
 * <p>
 * <strong>Two shapes, one vocabulary.</strong> The <em>preview</em> is the document as paper: the
 * shop and the document's title, number and status across the top, the other party and the dates
 * under it, the lines in a grey-headed table, the totals boxed at the bottom right. The
 * <em>entry form</em> is one card: who and what side by side, the lines as a table of inputs, notes
 * on the left and totals on the right, the buttons in a footer bar.
 * </p>
 *
 * <p>
 * <strong>The lines are a plain table, not `DataView`.</strong> `DataView` is for registers — rows
 * a person scans and opens. These are the lines of one document, read in order like the printed
 * sheet; below 640px the table scrolls sideways, as the template's does, rather than turning into
 * cards that would no longer read as a document.
 * </p>
 *
 * <p>
 * Text sizes come from the `--scale-*` properties, never Tailwind literals, so the person's text
 * size reaches every word here (CLAUDE.md, Visual language).
 * </p>
 */

const body = 'text-[length:var(--scale-font-size)]';
const meta = 'text-[length:var(--scale-list-meta)]';

/* ------------------------------------------------------------------ the page heading */

/**
 * The document heading, on the band every screen opens with (canvas "Purchase order flow",
 * 2026-10-25): a trail back to the register as the band's small line, the title in the serif, an
 * optional status beside it and a line under it, the actions on the right in the band's weights.
 * The trail's first word is a button, because sub-screens are internal state rather than addresses
 * (CLAUDE.md, Navigation).
 */
export function DocumentHeading({
  trail,
  current,
  title,
  subtitle,
  status,
  onBack,
  actions,
  overlap = false,
}: {
  /** The register this document belongs to — "Sales", "Purchase orders". */
  trail: string;
  /** This document's number, or "New". */
  current: string;
  title: string;
  subtitle?: ReactNode;
  status?: { label: string; tone: StatusTone } | undefined;
  onBack: () => void;
  actions?: ReactNode;
  /** Lets the card after the heading rise over the band's lower edge, as `PageHeading`'s does. */
  overlap?: boolean;
}) {
  return (
    <OnBandSurface>
      <div className={`page-band mb-3 lg:mb-5 ${overlap ? 'page-band-overlap' : ''}`}>
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div className="min-w-0">
            <div className="page-eyebrow flex items-center gap-1.5">
              <button
                type="button"
                onClick={onBack}
                className="inline-flex cursor-pointer items-center gap-1 uppercase hover:text-[#ffffff]"
              >
                <i className="icon icon-arrow-left" aria-hidden />
                {trail}
              </button>
              <i className="icon icon-chevron-right text-[10px]" aria-hidden />
              <span className="font-mono-nums">{current}</span>
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <h1 className="page-title">{title}</h1>
              {status && <StatusBadge tone={status.tone}>{status.label}</StatusBadge>}
            </div>
            {subtitle && <p className="page-subtitle mb-0">{subtitle}</p>}
          </div>
          {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
        </div>
      </div>
    </OnBandSurface>
  );
}

/* ------------------------------------------------------------------ the preview */

/** The paper: the soft card every screen sits on, with the template's generous `p-5 sm:p-8`. */
export function DocumentPaper({ children }: { children: ReactNode }) {
  return <div className="ar-card w-full p-5 sm:p-8">{children}</div>;
}

/** Who issued it on the left; the document's title, number and status on the right. */
export function DocumentTop({
  issuer,
  issuerLines,
  title,
  number,
  status,
}: {
  issuer: string;
  issuerLines: readonly (string | null | undefined)[];
  title: string;
  number: string;
  status?: { label: string; tone: StatusTone } | undefined;
}) {
  return (
    <div className="mb-8 flex flex-wrap items-start justify-between gap-5 lg:flex-nowrap">
      <div>
        <p className="text-title mb-2 text-lg font-bold">{issuer}</p>
        <div className={`text-default ${body}`}>
          {issuerLines
            .filter((line): line is string => Boolean(line))
            .map((line) => (
              <span key={line} className="block pt-1 first:pt-0">
                {line}
              </span>
            ))}
        </div>
      </div>
      <div className="text-start sm:text-right">
        <h2 className="text-title mb-1 text-xl font-bold uppercase">{title}</h2>
        <p className={`text-default font-mono-nums mb-0 ${body}`}>{number}</p>
        {status && (
          <span className="mt-2 inline-block">
            <StatusBadge tone={status.tone}>{status.label}</StatusBadge>
          </span>
        )}
      </div>
    </div>
  );
}

export interface DocumentFact {
  label: string;
  value: ReactNode;
}

/** The other party on the left ("Bill to", "Supplier"); dates and references on the right. */
export function DocumentParties({
  heading,
  name,
  lines,
  facts,
}: {
  heading: string;
  name: string;
  lines: readonly (string | null | undefined)[];
  facts: readonly DocumentFact[];
}) {
  return (
    <div className="border-border-color mb-6 grid grid-cols-1 gap-6 border-b pb-6 sm:grid-cols-2">
      <div>
        <p className={`text-default mb-2 ${body}`}>{heading}</p>
        <p className={`text-title mb-2 font-semibold ${body}`}>{name}</p>
        <p className={`text-default mb-0 ${body}`}>
          {lines
            .filter((line): line is string => Boolean(line))
            .map((line) => (
              <span key={line} className="block pt-1 first:pt-0">
                {line}
              </span>
            ))}
        </p>
      </div>
      <div className={`space-y-2 text-start sm:text-right ${body}`}>
        {facts.map((fact) => (
          <div key={fact.label}>
            <span className="text-default">{fact.label}:</span>{' '}
            <span className="font-semibold text-gray-900">{fact.value}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export interface DocumentColumn<T> {
  key: string;
  title: string;
  /** Figures right-aligned, as on the template's sheet. */
  numeric?: boolean;
  render: (row: T) => ReactNode;
}

/**
 * A document's lines: the template's grey-headed table. Also the entry form's items table — the
 * cells are then inputs, and the last column is the remove button.
 */
export function DocumentLines<T>({
  columns,
  rows,
  rowKey,
  empty,
}: {
  columns: readonly DocumentColumn<T>[];
  rows: readonly T[];
  rowKey: (row: T) => string | number;
  empty?: ReactNode;
}) {
  return (
    <div className="mb-6 overflow-x-auto">
      <table className={`w-full ${body}`}>
        <thead>
          <tr className="border-border-color bg-light text-default border-b">
            {columns.map((column) => (
              <th
                key={column.key}
                className={`px-3 py-3 font-semibold text-gray-900 ${column.numeric ? 'text-right' : 'text-left'}`}
              >
                {column.title}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 && empty ? (
            <tr>
              <td colSpan={columns.length} className={`text-default px-3 py-6 text-center ${body}`}>
                {empty}
              </td>
            </tr>
          ) : (
            rows.map((row) => (
              <tr key={rowKey(row)} className="border-border-color border-b align-top">
                {columns.map((column) => (
                  <td
                    key={column.key}
                    className={`px-3 py-3 ${column.numeric ? 'font-mono-nums text-right' : ''}`}
                  >
                    {column.render(row)}
                  </td>
                ))}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}

/** A line's name, with its muted second line under it — HSN, rate, what came off. */
export function LineName({ name, note }: { name: ReactNode; note?: ReactNode }) {
  return (
    <div className="min-w-40">
      <div className="text-title font-semibold">{name}</div>
      {note && <div className={`text-default mt-0.5 ${meta}`}>{note}</div>}
    </div>
  );
}

export interface TotalRow {
  label: string;
  value: string;
  /** The document's total: bold, the primary colour, a rule above it. */
  grand?: boolean;
}

/** The totals box at the bottom right. */
export function DocumentTotals({ rows }: { rows: readonly TotalRow[] }) {
  return (
    <div className="flex justify-end">
      <div className={`w-full max-w-72 space-y-2 ${body}`}>
        {rows.map((row) =>
          row.grand ? (
            <div
              key={row.label}
              className="border-border-color flex justify-between border-t pt-2 text-base"
            >
              <span className="text-title font-bold">{row.label}</span>
              <span className="text-primary font-mono-nums font-bold">{row.value}</span>
            </div>
          ) : (
            <div key={row.label} className="flex justify-between">
              <span className="text-default">{row.label}</span>
              <span className="font-mono-nums font-semibold text-gray-900">{row.value}</span>
            </div>
          ),
        )}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ the entry form */

/** The entry form's one card. */
export function DocumentForm({ children }: { children: ReactNode }) {
  return (
    <div className="border-border-color rounded-md border bg-white p-5 shadow-sm">{children}</div>
  );
}

/** Who and what, side by side, ruled off from the lines below. */
export function DocumentFormHead({ left, right }: { left: ReactNode; right: ReactNode }) {
  return (
    <div className="border-border-color mb-6 grid grid-cols-1 gap-8 border-b pb-6 md:grid-cols-2">
      <div className="flex flex-col gap-4">{left}</div>
      <div className="flex flex-col gap-4">{right}</div>
    </div>
  );
}

/** A section heading inside the form — "Customer", "Sale details", "Items". */
export function FormSectionTitle({ children, aside }: { children: ReactNode; aside?: ReactNode }) {
  return (
    <div className="mb-1 flex flex-wrap items-center justify-between gap-2">
      <h2 className="text-lg font-bold text-gray-900">{children}</h2>
      {aside}
    </div>
  );
}

/** Notes and payments on the left, totals on the right — the template's 7/5 split. */
export function DocumentFormFoot({ left, right }: { left: ReactNode; right: ReactNode }) {
  return (
    <div className="grid grid-cols-1 gap-8 xl:grid-cols-12">
      <div className="flex flex-col gap-4 xl:col-span-7">{left}</div>
      <div className="flex flex-col gap-4 xl:col-span-5">{right}</div>
    </div>
  );
}

/** The buttons, ruled off at the bottom right. */
export function DocumentFormActions({ children }: { children: ReactNode }) {
  return (
    <div className="border-border-color mt-5 flex flex-wrap items-center justify-end gap-2 border-t pt-5">
      {children}
    </div>
  );
}
